<template>
  <div class="relative">
    <poppins-notification
      id="profile-link-toast"
      class="-mt-16 md-1:mt-8"
      message="Sitter profile link copied to clipboard."
      :triggerNotification="showToast"
      @toggle-visibility="closeToast"
    />
    <div class="flex justify-between items-center flex-wrap gap-16">
      <poppins-breadcrumb :header="breadcrumbInfo.header" class="!mt-0" :href="breadcrumbInfo.link" anchorLink />
      <div v-if="accountTypeInfo.isParent" class="flex gap-8">
        <provider-profile-favorite-sitter-button
          :favoritedSitter="favoritedSitter"
          :sitterAccountId="sitterAccountId"
          class="self-center"
          @favorited-sitter-change="favoritedSitterChange"
        />
        <action-menu :menuOptions="kebabOptions" iconType="kebab" noBackground />
      </div>
      <div v-else-if="!loading && isProfileOwner" class="flex items-center w-full justify-between sm:w-auto">
        <profile-edit-menu class="w-[270px] mr-8" :careTypeForLabel="careTypeLabel" :profileAccountData="profileAccountData" />
        <action-menu
          id="profile-owner-action-menu"
          :menuOptions="kebabOptions"
          :menuListLabel="menuListLabel"
          iconType="kebab"
          noBackground
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import kebabCase from 'lodash/kebabCase';
import PoppinsBreadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import ActionMenu from '@/components/action_menu/ActionMenu.vue';
import PoppinsNotification from '@/components/notification/Notification.vue';
import ProviderProfileFavoriteSitterButton from './ProviderProfileFavoriteSitterButton.vue';
import { MenuItemOptions } from '@/components/action_menu/__types__/ActionMenuTypes';
import { BannerType } from '../__types__/BannerTypes';

import WarningOutlineIcon from '@/graphics/icons/WarningOutlineIcon.vue';
import ShareIcon from '@/graphics/icons/ShareIcon.vue';
import ProfileEditMenu from '@/apps/account/provider/profile/ProfileEditMenu.vue';

export default {
  name: 'ProviderProfileActionBar',
  components: {
    PoppinsBreadcrumb,
    ActionMenu,
    PoppinsNotification,
    ProviderProfileFavoriteSitterButton,
    ProfileEditMenu,
  },
  props: {
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    viewerAccountData: {
      default: () => ({}),
      type: Object,
    },
    favoritedSitter: {
      default: false,
      type: Boolean,
    },
    isProfileOwner: {
      default: false,
      type: Boolean,
    },
    bannerType: {
      default: '',
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showToast: false,
    };
  },
  computed: {
    sitterAccountId(): string {
      return this.profileAccountData?.id || '';
    },
    parentZipcode(): string {
      return this.viewerAccountData?.location?.zipcode || '';
    },
    favoritedLabel(): string {
      if (this.favoritedSitter) {
        return 'Unfavorite sitter';
      }
      return 'Favorite sitter';
    },
    careTypeLabel(): string {
      return this.$route.params.care_type;
    },
    caretypeLink(): string {
      // babysitter & pet sitting links are different than the care type name + require special cases
      if (this.$route.params.care_type === 'babysitter') {
        return `/babysitters`;
      }
      if (this.$route.params.care_type === 'pet_care') {
        return `/pet-sitting`;
      }
      // assuming care_type is companion_care, special_needs, nanny, housekeeping
      return `/${kebabCase(this.$route.params.care_type)}`;
    },
    menuListLabel(): string {
      return `${this.$route.params.care_type.split('_').join(' ')} Services`;
    },
    isEditView(): boolean {
      return this.bannerType === BannerType.PROFILE_EDIT_MODE;
    },
    isHiddenProfile(): boolean {
      return this.bannerType === BannerType.PROFILE_HIDDEN;
    },
    kebabOptions(): MenuItemOptions[] {
      if (this.isProfileOwner) {
        return [
          {
            labelText: this.isEditView || this.isHiddenProfile ? 'Preview' : 'Edit',
            labelClasses: 'text-s font-medium ml-12 text-grey-clickable my-8',
            click: () => this.toggleProfileMode(),
          },
          {
            labelText: this.isHiddenProfile ? 'Unhide' : 'Hide',
            labelClasses: 'text-s font-medium ml-12 text-critical my-8',
            click: () => this.toggleProfileVisibility(),
          },
        ];
      }

      if (this.accountTypeInfo.isParent) {
        return [
          {
            icon: WarningOutlineIcon,
            height: 24,
            width: 24,
            labelText: 'Report sitter',
            href: `https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=360000078194&account_id=${this.sitterAccountId}`,
            target: '_blank',
            click: () => {},
          },
          {
            icon: ShareIcon,
            height: 24,
            width: 24,
            labelText: 'Share profile',
            closeOnClick: true,
            click: () => {
              navigator.clipboard.writeText(window.location.toString());
              this.showToast = true;
              setTimeout(() => {
                const toastEl = document.getElementById('profile-link-toast');
                if (toastEl) {
                  toastEl.focus();
                }
                document.body.scrollTop = document.documentElement.scrollTop = 0;
              }, 1);
            },
          },
        ];
      }
      return [];
    },
    breadcrumbInfo(): any {
      if (!this.accountTypeInfo?.accountType) {
        return {
          header: 'More',
          link: this.caretypeLink,
        };
      }
      if (this.accountTypeInfo.isParent) {
        const oldSearchCareTypes = ['pet_care', 'housekeeping', 'companion_care', 'special_needs'];
        if (oldSearchCareTypes.includes(this.$route.params.care_type)) {
          return {
            header: 'Search',
            link: `/profile/provider/search?search_strategy=${this.$route.params.care_type}&zipcode=${this.parentZipcode}`,
          };
        }
        return {
          header: 'Search',
          link: `/availability/search?careType=${this.$route.params.care_type}&zipcode=${this.parentZipcode}`,
        };
      }
      if (this.accountTypeInfo.isSitter) {
        return {
          header: 'Dashboard',
          link: `/account/providers/dashboard`,
        };
      }
      return null;
    },
  },
  methods: {
    favoritedSitterChange(favoritedSitter) {
      this.$emit('favoritedSitterChange', favoritedSitter);
    },
    closeToast() {
      this.showToast = false;
    },
    toggleProfileMode() {
      if (this.isHiddenProfile) {
        // Show popup to unhide profile before allowing preview mode
        this.$emit('show-edit-mode-popup', 'PREVIEW_UNHIDE');
      } else if (this.isEditView) {
        this.$emit('show-preview-mode');
      } else {
        this.$emit('remove-preview-mode');
      }
    },
    toggleProfileVisibility() {
      if (!this.isHiddenProfile) {
        this.$emit('show-edit-mode-popup', 'CONFIRM_HIDE');
      } else {
        this.$emit('toggle-profile-visibility');
      }
    },
  },
};
</script>

<style>
#provider-profile #profile-owner-action-menu ul {
  min-width: 240px;
}
</style>
